import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trash2, Plus, ChevronRight, Check } from 'lucide-react';
import useKeyActivityState, { IKeyActivity } from '@/states/key-activities.state';
import useOrgChartState from '@/states/organization.state';
import { useIsReadOnly } from '@/utils/auth/roleChecker';
import useCompanyState from '@/states/company.state';
import { useCompanyProfileSteps } from '@/hooks/useCompanyProfileSteps';
import useKeyActivityHandler, { InlineRow } from '@/hooks/useKeyActivityHandler';
import { buildNodeLevelMap, sortByOrgLevel } from '@/utils/functions/sortRows';
import StepTipsPanel from '@/components/company/StepTipsPanel';

// Slot KA1..KA4 = empat record pertama jabatan itu menurut id naik (urutan yang sama dengan snapshot simpan di hook).
function buildInlineRows(grouped: IKeyActivity[], flatKAs: IKeyActivity[], newTempId: () => string): InlineRow[] {
  return grouped.map(row => {
    const nodeId = row.org_chart_node_id ?? null;
    const slots = flatKAs
      .filter(ka => ka.org_chart_node_id === nodeId)
      .sort((a, b) => Number(a.id) - Number(b.id))
      .slice(0, 4);
    return {
      tempId: newTempId(),
      org_chart_node_id: nodeId ? String(nodeId) : '',
      ka1: slots[0]?.name?.trim() ?? '',
      ka2: slots[1]?.name?.trim() ?? '',
      ka3: slots[2]?.name?.trim() ?? '',
      ka4: slots[3]?.name?.trim() ?? '',
    };
  });
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function KeyActivities() {
  const isReadOnly = useIsReadOnly();
  const { tenant_id } = useParams();
  const navigate = useNavigate();

  const { keyActivities, groupedData, isLoading } = useKeyActivityState();

  const { orgCharts } = useOrgChartState();

  const {
    rows,
    setRows,
    isSaving,
    dataReady,
    showPanel,
    setShowPanel,
    fetchSeq,
    allRoles,
    newTempId,
    updateRow,
    addRow,
    removeRow,
    handleSaveAndNext,
  } = useKeyActivityHandler();

  useCompanyProfileSteps();
  const { companyProfileSteps } = useCompanyState();
  const keyActivitiesStep = companyProfileSteps.find(step => step.step_name.trim().toLowerCase() === 'key activities');
  const videoLink = keyActivitiesStep?.video_link;
  const tips = keyActivitiesStep?.tips;

  const levelMap = useMemo(() => buildNodeLevelMap(orgCharts), [orgCharts]);

  // Seed rows once after every completed fetchData call (fetchSeq increments on completion).
  useEffect(() => {
    if (fetchSeq === 0) return;
    const content = groupedData?.content ?? [];
    const sorted = sortByOrgLevel(
      content as IKeyActivity[],
      r => r.org_chart_node_id ?? null,
      levelMap,
      r => r.org_chart_node?.name ?? '',
      'asc',
    );
    setRows(buildInlineRows(sorted, keyActivities, newTempId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSeq]);

  return (
    <section className="w-full">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 p-5 flex flex-col md:flex-row md:items-center justify-between gap-2 shadow-sm">
        <div className="flex flex-col">
          <h1 className="font-semibold text-lg">Key Activities</h1>
          <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
            Menentukan 3 tugas utama setiap jabatan dalam organization structure.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setShowPanel(v => !v)}
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ChevronRight size={15} className={`transition-transform duration-200 ${showPanel ? 'rotate-90' : ''}`} />
            {showPanel ? 'Sembunyikan Panduan' : 'Tonton Video Panduan'}
          </button>

          {!isReadOnly && (
            <button
              type="button"
              onClick={() => handleSaveAndNext(() => navigate(`/${tenant_id}/job-description`))}
              disabled={isSaving || !dataReady}
              className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-white font-medium transition-opacity disabled:opacity-60"
              style={{ background: '#FD6118' }}
            >
              <Check size={15} />
              {isSaving ? 'Menyimpan...' : 'Simpan & Lanjut'}
            </button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col xl:flex-row gap-6 p-5">
        <div className="flex-1 min-w-0">
          {isLoading && rows.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-10">Memuat data...</p>
          ) : (
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-[860px] w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                      <th className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left w-[160px] sticky left-0 bg-gray-50 dark:bg-gray-800 z-10">
                        JABATAN <span className="text-red-400">*</span>
                      </th>
                      {(['KEY ACTIVITY 1', 'KEY ACTIVITY 2', 'KEY ACTIVITY 3', 'KEY ACTIVITY 4'] as const).map(
                        label => (
                          <th
                            key={label}
                            className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left"
                          >
                            {label} <span className="text-red-400">*</span>
                          </th>
                        ),
                      )}
                      <th className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-center w-20">
                        AKSI
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(row => (
                      <tr
                        key={row.tempId}
                        className="group border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50/40 dark:hover:bg-gray-800/50"
                      >
                        {/* Jabatan */}
                        <td className="px-2 py-2 sticky left-0 bg-white dark:bg-gray-900 group-hover:bg-gray-50 dark:group-hover:bg-gray-800 z-10">
                          <select
                            value={row.org_chart_node_id}
                            onChange={e => updateRow(row.tempId, { org_chart_node_id: e.target.value })}
                            disabled={isReadOnly}
                            className="text-xs w-full h-8 px-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:text-gray-400"
                          >
                            <option value="">-- Pilih Jabatan --</option>
                            {allRoles
                              .filter(
                                role =>
                                  !rows.some(r => r.tempId !== row.tempId && r.org_chart_node_id === String(role.id)),
                              )
                              .map(role => (
                                <option key={role.id} value={String(role.id)}>
                                  {role.name}
                                </option>
                              ))}
                          </select>
                        </td>
                        {/* KA 1–4 */}
                        {(['ka1', 'ka2', 'ka3', 'ka4'] as const).map(field => (
                          <td key={field} className="px-2 py-2">
                            <input
                              type="text"
                              value={row[field]}
                              onChange={e => updateRow(row.tempId, { [field]: e.target.value })}
                              disabled={isReadOnly || !row.org_chart_node_id}
                              placeholder={
                                row.org_chart_node_id ? `Aktivitas ${field.slice(2)}` : 'Pilih Jabatan terlebih dahulu'
                              }
                              className="text-xs w-full h-8 px-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:text-gray-400"
                            />
                          </td>
                        ))}
                        {/* Aksi */}
                        <td className="px-1 py-2">
                          <div className="flex items-center justify-center">
                            {!isReadOnly && (
                              <button
                                type="button"
                                title="Hapus baris"
                                onClick={() => removeRow(row.tempId)}
                                className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                              >
                                <Trash2 size={15} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* + Tambah Baris */}
              {!isReadOnly && (
                <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={addRow}
                    className="flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
                  >
                    <Plus size={15} />
                    Tambah Baris
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {showPanel && (
          <div className="w-full xl:w-80 shrink-0 min-w-0">
            <StepTipsPanel tips={tips} videoLink={videoLink} />
          </div>
        )}
      </div>
    </section>
  );
}
